<template>
  <div class="customer-transactions">
    <!-- Filter Section -->
    <div class="mb-5">
      <b-row class="mb-3">
        <b-col md="3">
          <label>Từ ngày:</label>
          <date-picker
            placeholder="Chọn ngày bắt đầu"
            class="form-control-sm"
            :config="dateConfig"
            v-model="filters.fromDate"
          ></date-picker>
        </b-col>

        <b-col md="3">
          <label>Đến ngày:</label>
          <date-picker
            placeholder="Chọn ngày kết thúc"
            class="form-control-sm"
            :config="dateConfig"
            v-model="filters.toDate"
          ></date-picker>
        </b-col>

        <b-col md="3">
          <label>Loại giao dịch:</label>
          <b-form-select
            size="sm"
            v-model="filters.type"
            :options="transactionTypes"
            class="select-style"
          ></b-form-select>
        </b-col>

        <b-col md="3">
          <label>Nguồn gốc:</label>
          <b-form-select
            size="sm"
            v-model="filters.source"
            :options="sourcesOptions"
            class="select-style"
          ></b-form-select>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col md="3">
          <label>Trạng thái:</label>
          <b-form-select
            size="sm"
            v-model="filters.status"
            :options="statusOptions"
            class="select-style"
          ></b-form-select>
        </b-col>

        <b-col md="5">
          <label>Tìm kiếm:</label>
          <b-input-group>
            <b-form-input
              size="sm"
              placeholder="Tìm trong mô tả hoặc ID nguồn"
              v-model="filters.search"
              v-on:keyup.enter="applyFilters"
            ></b-form-input>
            <b-input-group-append>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="filters.search = ''"
              >
                <i class="fa fa-times"></i>
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="applyFilters"
              >
                <i class="fa fa-search"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>

        <b-col md="4" class="d-flex align-items-end">
          <b-button
            variant="primary"
            size="sm"
            class="mr-2"
            @click="applyFilters"
          >
            Lọc
          </b-button>
          <b-button variant="outline-secondary" size="sm" @click="resetFilters">
            Xóa lọc
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Transactions Table -->
    <b-table
      :items="transactions"
      :fields="tableFields"
      bordered
      hover
      :busy="isLoading"
      show-empty
      empty-text="Không có dữ liệu giao dịch điểm"
    >
      <template v-slot:table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Đang tải...</strong>
        </div>
      </template>

      <template v-slot:cell(index)="data">
        {{ data.index + 1 }}
      </template>

      <template v-slot:cell(transactionType)="row">
        <b-badge :variant="getTypeVariant(row.item.transactionType)">
          {{ formatTransactionType(row.item.transactionType) }}
        </b-badge>
      </template>

      <template v-slot:cell(points)="row">
        <span :class="row.item.points > 0 ? 'text-success' : 'text-danger'">
          {{ row.item.points > 0 ? '+' : '' }}{{ row.item.points }}
        </span>
      </template>

      <template v-slot:cell(source)="row">
        {{ formatTransactionSource(row.item.source) }}
        <small v-if="row.item.sourceId" class="d-block text-muted">
          ID: {{ row.item.sourceId }}
        </small>
      </template>

      <template v-slot:cell(status)="row">
        <b-badge :variant="getStatusVariant(row.item.status)">
          {{ formatTransactionStatus(row.item.status) }}
        </b-badge>
      </template>

      <template v-slot:cell(createdAt)="row">
        {{ formatDate(row.item.createdAt) }}
      </template>

      <!-- <template v-slot:cell(actions)="row">
        <b-button
          v-if="row.item.status === 'PENDING'"
          variant="outline-danger"
          size="sm"
          @click="cancelTransaction(row.item)"
        >
          Hủy
        </b-button>
      </template> -->
    </b-table>

    <!-- Pagination -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <p class="text-muted mb-0">
        Tổng số: <strong>{{ totalItems }}</strong> giao dịch
      </p>
      <b-pagination
        v-if="totalPages > 1"
        v-model="currentPage"
        :total-rows="totalItems"
        :per-page="10"
        align="right"
        size="sm"
        @change="onPageChange"
        first-class="page-item-first btn btn-icon btn-sm mr-1 my-1"
        prev-class="page-item-prev btn btn-icon btn-sm mr-.5 my-1"
        next-class="page-item-next btn btn-icon btn-sm mr-1 my-1 ml-.5"
        last-class="page-item-last btn btn-icon btn-sm my-1"
        page-class="btn btn-icon btn-sm border-0 mr-.5 my-1"
      >
        <template v-slot:first-text>
          <i class="ki ki-bold-double-arrow-back icon-xs"></i>
        </template>
        <template v-slot:prev-text>
          <i class="ki ki-bold-arrow-back icon-xs"></i>
        </template>
        <template v-slot:next-text>
          <i class="ki ki-bold-arrow-next icon-xs"></i>
        </template>
        <template v-slot:last-text>
          <i class="ki ki-bold-double-arrow-next icon-xs"></i>
        </template>
      </b-pagination>
    </div>

    <!-- Confirmation Modal for Cancel -->
    <b-modal
      v-model="showCancelModal"
      title="Xác nhận hủy giao dịch"
      header-bg-variant="light"
      ok-variant="danger"
      ok-title="Hủy giao dịch"
      cancel-title="Đóng"
      @ok="confirmCancelTransaction"
    >
      <p>Bạn có chắc chắn muốn hủy giao dịch này không?</p>
      <p class="text-muted small">Hành động này không thể hoàn tác.</p>
    </b-modal>

    <!-- Add the adjustment modal -->
    <point-adjustment-modal
      :value="adjustmentModalVisible"
      @input="adjustmentModalVisible = $event"
      :customer-id="customerId"
      :current-points="customerPoints"
      @adjustment-success="onAdjustmentSuccess"
    />
  </div>
</template>

<script>
import { defineComponent, watch, onMounted, ref, onUnmounted } from 'vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import useCustomerTransactions from '../composables/useCustomerTransaction';
import emitter from '@/core/services/emitter';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import PointAdjustmentModal from './PointAdjustmentModal.vue';

export default defineComponent({
  name: 'CustomerTransactionsTab',

  components: {
    datePicker,
    PointAdjustmentModal,
  },

  props: {
    customerId: {
      type: [String, Number],
      required: true,
    },
  },

  setup(props) {
    const {
      transactions,
      totalItems,
      totalPages,
      currentPage,
      isLoading,
      filters,
      transactionTypes,
      sourcesOptions,
      statusOptions,
      fetchTransactions,
      applyFilters: applyTransactionFilters,
      resetFilters: resetTransactionFilters,
      formatTransactionType,
      formatTransactionSource,
      formatTransactionStatus,
      getStatusVariant,
      getTypeVariant,
      formatDate,
    } = useCustomerTransactions();

    const tableFields = [
      {
        key: 'index',
        label: 'STT',
        thStyle: { width: '60px' },
        class: 'text-center',
      },
      {
        key: 'transactionType',
        label: 'Loại giao dịch',
        thStyle: { width: '120px' },
        class: 'text-center',
      },
      {
        key: 'points',
        label: 'Điểm',
        thStyle: { width: '100px' },
        class: 'text-center',
      },
      {
        key: 'pointsBefore',
        label: 'Điểm trước',
        thStyle: { width: '100px' },
        class: 'text-center',
      },
      {
        key: 'pointsAfter',
        label: 'Điểm sau',
        thStyle: { width: '100px' },
        class: 'text-center',
      },
      { key: 'source', label: 'Nguồn gốc', thStyle: { width: '150px' } },
      { key: 'description', label: 'Mô tả' },
      {
        key: 'status',
        label: 'Trạng thái',
        thStyle: { width: '100px' },
        class: 'text-center',
      },
      { key: 'createdAt', label: 'Ngày tạo', thStyle: { width: '150px' } },
      // { key: 'actions', label: '', thStyle: { width: '80px' } },
    ];

    const dateConfig = {
      format: 'DD/MM/YYYY',
      useCurrent: false,
      showClear: true,
      showClose: true,
      locale: 'vi',
      sideBySide: true,
    };

    // Cancel transaction functionality
    const showCancelModal = ref(false);
    const selectedTransaction = ref(null);
    const adjustmentModalVisible = ref(false);
    const customerPoints = ref(0);

    const cancelTransaction = (transaction) => {
      selectedTransaction.value = transaction;
      showCancelModal.value = true;
    };

    const confirmCancelTransaction = async () => {
      if (!selectedTransaction.value) return;

      try {
        await ApiService.post('/customer-points/cancel', {
          transactionId: selectedTransaction.value.id,
          reason: 'Hủy bởi người dùng',
          sourceId: selectedTransaction.value.sourceId,
        });

        makeToastSuccess('Giao dịch đã được hủy thành công');
        fetchTransactions(props.customerId, currentPage.value);
      } catch (error) {
        console.error('Error cancelling transaction:', error);
        makeToastFaile('Không thể hủy giao dịch');
      } finally {
        showCancelModal.value = false;
        selectedTransaction.value = null;
      }
    };

    const fetchCustomerPoints = async () => {
      try {
        const response = await ApiService.get(
          `/v1/customer-points/${props.customerId}`,
        );

        if (response.data && response.data.value) {
          customerPoints.value = response.data.value.currentPoints || 0;
        } else if (response.data) {
          customerPoints.value = response.data.currentPoints || 0;
        }
      } catch (error) {
        console.error('Error fetching customer points:', error);
      }
    };

    const showAdjustModal = () => {
      fetchCustomerPoints();
      adjustmentModalVisible.value = true;
    };

    const onAdjustmentSuccess = () => {
      fetchTransactions(props.customerId, currentPage.value);
    };

    const applyFilters = () => {
      applyTransactionFilters(props.customerId);
    };

    const resetFilters = () => {
      resetTransactionFilters(props.customerId);
    };

    const onPageChange = (page) => {
      fetchTransactions(props.customerId, page);
    };

    watch(
      () => props.customerId,
      (newId) => {
        if (newId) {
          fetchTransactions(newId);
        }
      },
      { immediate: true },
    );

    onMounted(() => {
      if (props.customerId) {
        fetchTransactions(props.customerId);
      }

      // Listen for points-updated event
      emitter.on('points-updated', () => {
        console.log('Points updated event received, refreshing transactions');
        fetchTransactions(props.customerId, currentPage.value);
      });
    });

    // Clean up event listener when component is unmounted
    onUnmounted(() => {
      emitter.off('points-updated');
    });

    return {
      transactions,
      totalItems,
      totalPages,
      currentPage,
      isLoading,
      filters,
      transactionTypes,
      sourcesOptions,
      statusOptions,
      tableFields,
      dateConfig,
      showCancelModal,
      selectedTransaction,
      formatTransactionType,
      formatTransactionSource,
      formatTransactionStatus,
      getStatusVariant,
      getTypeVariant,
      formatDate,
      applyFilters,
      resetFilters,
      onPageChange,
      cancelTransaction,
      confirmCancelTransaction,
      adjustmentModalVisible,
      customerPoints,
      showAdjustModal,
      onAdjustmentSuccess,
    };
  },
});
</script>

<style scoped>
.select-style {
  border-radius: 0.28rem;
  box-shadow: none;
  border: 1px solid #ced4da;
}

.customer-transactions {
  padding: 1rem 0;
}
</style>
